"use client";

import { ArrowRight, Globe, Heart, Target, Users, Zap } from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";

const values = [
  {
    icon: Target,
    title: "Clarity over complexity",
    description:
      "We believe automation tools should be understandable. If you can't see what's happening, you can't trust it.",
  },
  {
    icon: Zap,
    title: "Reliability by default",
    description:
      "Production workflows need to work. We build with retries, observability, and error handling from day one.",
  },
  {
    icon: Heart,
    title: "Developer empathy",
    description:
      "We're developers too. We build the tools we wish we had when debugging automation at 2am.",
  },
];

const milestones = [
  {
    year: "2024",
    title: "Founded",
    description: "Started building the workflow platform we wished existed",
  },
  {
    year: "2024",
    title: "Public beta",
    description: "Launched to early adopters and teams",
  },
  {
    year: "2025",
    title: "What's next",
    description: "Building the future of observable automation",
  },
];

export default function AboutPage() {
  const navigate = useRouter();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-20 pointer-events-none" />
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeInUp className="max-w-3xl mx-auto text-center">
            {/* <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 mb-6 shadow-lg shadow-primary/10"
              whileHover={{ scale: 1.05, rotate: 5 }}
            >
              <Rocket className="w-8 h-8 text-primary" />
            </motion.div> */}
            <h1 className="text-foreground mb-6">
              Building automation you can
              <br />
              <span className="gradient-text">actually trust</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We're a small team on a mission to make workflow automation
              transparent, reliable, and accessible to every team.
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-foreground mb-4">
                    Why we're building this
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We've all been there: a workflow breaks at 2am, and you have
                    no idea what went wrong. The logs are cryptic, the retry
                    happened somewhere, and you're left guessing.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We started Makeplans because we were tired of automation
                    tools that felt like black boxes. We wanted something where
                    you could see exactly what happened, debug in seconds, and
                    actually trust that your automations were working.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    That's what we're building: workflow automation with clarity
                    at its core.
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl blur-xl" />
                  <div className="relative p-8 rounded-xl bg-card border border-border">
                    <blockquote className="text-lg font-medium text-foreground mb-4">
                      "When something breaks, you should know exactly what
                      happened and how to fix it."
                    </blockquote>
                    <p className="text-sm text-muted-foreground">
                      - Our founding principle
                    </p>
                  </div>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              What we believe
            </h2>
            <p className="text-lg text-muted-foreground">
              The principles that guide how we build
            </p>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full p-8 rounded-xl card-interactive text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 mx-auto">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Our journey
            </h2>
            <p className="text-lg text-muted-foreground">
              From idea to public beta
            </p>
          </FadeInUp>

          <div className="max-w-2xl mx-auto">
            {milestones.map((milestone, index) => (
              <FadeInUp key={index} delay={index * 0.1}>
                <div className="flex gap-6 mb-8 last:mb-0">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-sm font-semibold text-primary">
                      {milestone.year.slice(2)}
                    </div>
                    {index < milestones.length - 1 && (
                      <div className="w-px h-full bg-border mt-2" />
                    )}
                  </div>
                  <div className="pb-8">
                    <h3 className="font-semibold text-foreground mb-1">
                      {milestone.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* Team placeholder */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted mb-6">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              A small team, building big things
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're a small, focused team. We ship fast, listen closely, and
              care deeply about what we're building. Team details coming soon.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="gradient"
                size="lg"
                onClick={() => navigate.push("/contact")}
              >
                Get in touch
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <Globe className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Join us on this journey
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're just getting started. Try Makeplans today and help us build
              the future of workflow automation.
            </p>
            <Button
              variant="gradient"
              size="lg"
              onClick={() => navigate.push("/register")}
            >
              Start building free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </FadeInUp>
        </div>
      </section>

      <Footer />
    </div>
  );
}
